import { usePropertyField } from '@vibesites/runtime/react';

export type PropertyCountry = 'us' | 'ca' | null;

const US_COUNTRIES = new Set([
  'us',
  'usa',
  'united states',
  'united states of america',
  'america',
]);

const CA_COUNTRIES = new Set(['ca', 'can', 'canada']);

/** Province codes and names. "CA" is California, not a province. */
const CA_PROVINCES = new Set([
  'ab', 'bc', 'mb', 'nb', 'nl', 'ns', 'nt', 'nu', 'on', 'pe', 'pei', 'qc', 'sk', 'yt',
  'alberta', 'british columbia', 'manitoba', 'new brunswick',
  'newfoundland and labrador', 'newfoundland', 'nova scotia',
  'northwest territories', 'nunavut', 'ontario', 'prince edward island',
  'quebec', 'québec', 'saskatchewan', 'yukon', 'yukon territory',
]);

const US_STATES = new Set([
  'al', 'ak', 'az', 'ar', 'ca', 'co', 'ct', 'de', 'dc', 'fl', 'ga', 'hi', 'id', 'il', 'in', 'ia',
  'ks', 'ky', 'la', 'me', 'md', 'ma', 'mi', 'mn', 'ms', 'mo', 'mt', 'ne', 'nv', 'nh', 'nj', 'nm',
  'ny', 'nc', 'nd', 'oh', 'ok', 'or', 'pa', 'ri', 'sc', 'sd', 'tn', 'tx', 'ut', 'vt', 'va', 'wa',
  'wv', 'wi', 'wy',
  'alabama', 'alaska', 'arizona', 'arkansas', 'california', 'colorado', 'connecticut',
  'delaware', 'district of columbia', 'florida', 'georgia', 'hawaii', 'idaho', 'illinois',
  'indiana', 'iowa', 'kansas', 'kentucky', 'louisiana', 'maine', 'maryland', 'massachusetts',
  'michigan', 'minnesota', 'mississippi', 'missouri', 'montana', 'nebraska', 'nevada',
  'new hampshire', 'new jersey', 'new mexico', 'new york', 'north carolina', 'north dakota',
  'ohio', 'oklahoma', 'oregon', 'pennsylvania', 'rhode island', 'south carolina', 'south dakota',
  'tennessee', 'texas', 'utah', 'vermont', 'virginia', 'washington', 'west virginia',
  'wisconsin', 'wyoming',
]);

function normalizePlace(value: string | null | undefined): string {
  return (value || '').trim().toLowerCase().replace(/\./g, '');
}

/**
 * Resolves the property's country from its address fields. Country wins;
 * state, postal code, and full address cover records that leave country blank.
 * Returns null for other countries or when nothing identifies the country.
 */
export function resolvePropertyCountry(
  country: string | null | undefined,
  state: string | null | undefined,
  postal: string | null | undefined,
  fullAddress: string | null | undefined,
): PropertyCountry {
  const countryKey = normalizePlace(country);
  if (US_COUNTRIES.has(countryKey)) return 'us';
  if (CA_COUNTRIES.has(countryKey)) return 'ca';
  if (countryKey) return null;

  const stateKey = normalizePlace(state);
  if (CA_PROVINCES.has(stateKey)) return 'ca';
  if (US_STATES.has(stateKey)) return 'us';

  const zip = (postal || '').trim().toUpperCase();
  if (/^[A-Z]\d[A-Z][ -]?\d[A-Z]\d$/.test(zip)) return 'ca';
  if (/^\d{5}(?:-\d{4})?$/.test(zip)) return 'us';

  const address = fullAddress || '';
  if (/\bcanada\b/i.test(address)) return 'ca';
  if (/\bunited states\b|\bu\.s\.a\.?\b|\busa\b/i.test(address)) return 'us';

  return null;
}

/** Swaps US spellings for Canadian ones in fixed UI labels (e.g. nav page names). */
export function localizeSpelling(text: string, country: PropertyCountry): string {
  if (country !== 'ca') return text;
  return text.replace(/(neighb)or(hood)/gi, '$1our$2');
}

export function usePropertyCountry(): PropertyCountry {
  const country = usePropertyField('property_address_country');
  const state = usePropertyField('property_address_state');
  const zip = usePropertyField('property_address_zip');
  const zipFull = usePropertyField('property_address_zip_full');
  const fullAddress = usePropertyField('property_full_address');
  return resolvePropertyCountry(country, state, zip || zipFull, fullAddress);
}
